import { useState } from 'react';
import { Button, Input, Modal, Segmented, Tooltip, Typography } from 'antd';
import { CompressOutlined, UndoOutlined, RedoOutlined } from '@ant-design/icons';
import type { FieldMeta, TableContext } from '../api/types';
import type { ContractRenderData } from '../contract/types';
import type {
  ContractSection,
  ContractSectionType,
  ContractTemplate,
  OverlayItem,
} from '../contract/types';
import { INSERTABLE_SECTION_TYPES, SECTION_TYPE_LABELS } from '../contract/types';
import { ContractPreview } from '../components/ContractMode/ContractPreview';
import { CanvasHints } from '../components/shell/EditorGuide';
import { EditorTour, useEditorTour } from '../components/shell/EditorTour';
import { useTemplateHistory } from '../contract/useHistory';
import { ShareToggle } from '../components/shell/ShareToggle';
import { ColumnsEditor } from '../components/ContractMode/editors/ColumnsEditor';
import { SECTION_ICONS } from '../components/ContractMode/sectionIcons';
import {
  findSectionDeep,
  insertAtDeep,
  moveSectionDeep,
  removeSectionDeep,
  reorderDeep,
  updateSectionDeep,
} from '../../render-core/sectionTree';
import { CellStylePanel } from '../components/ContractMode/editors/CellStylePanel';
import type { CellProps, Rect } from '../contract/freeTable';
import { createFreeTable } from '../contract/freeTable';
import { genId } from '../contract/types';
import type { FreeTableSection } from '../contract/types';
import { FieldPalette } from './FieldPalette';
import { buildDemoSections } from '../templates/demoCanvas';
import { createDefaultSection } from '../components/ContractMode/SectionEditor';
import { appendCellText, applyCellPatch, applyCellText, applySectionText, applyTableOp, convertSection, createBlock, duplicateSection, insertAfter, rebindField, type TableOpMessage } from '../contract/canvasOps';
import { OverlaysEditor } from '../components/ContractMode/editors/OverlaysEditor';
import { ContractSettings } from '../components/ContractMode/ContractSettings';
import { HeaderEditor } from '../components/ContractMode/editors/HeaderEditor';
import { InfoGridEditor } from '../components/ContractMode/editors/InfoGridEditor';
import { TextBlockEditor } from '../components/ContractMode/editors/TextBlockEditor';
import { DetailTableEditor } from '../components/ContractMode/editors/DetailTableEditor';
import { FreeTableEditor } from '../components/ContractMode/editors/FreeTableEditor';
import { SignatureEditor } from '../components/ContractMode/editors/SignatureEditor';
import { AttachmentEditor } from '../components/ContractMode/editors/AttachmentEditor';
import { CodeEditor } from '../components/ContractMode/editors/CodeEditor';
import { RichTextEditor } from '../components/ContractMode/editors/RichTextEditor';
import { DividerEditor } from '../components/ContractMode/editors/DividerEditor';
import { VisibilityRuleEditor } from '../components/ContractMode/editors/VisibilityRuleEditor';
import type { VisibilityRule } from '../contract/types';

interface Props {
  context: TableContext | null;
  recordId: string | null;
  template: ContractTemplate;
  fieldMetas: FieldMeta[];
  /** 局部更新模板。 */
  onUpdate: (patch: Partial<Pick<ContractTemplate, 'name' | 'sections' | 'settings' | 'overlays' | 'visibility' | 'shared' | 'sharedBy'>>) => void;
  onClose: () => void;
  /** 独立标签页里没有 SDK，用侧边栏存下的渲染数据快照。 */
  staticData?: ContractRenderData | null;
  /** 独立标签页模式：文案改成「关闭本页」，并提示改动会同步回侧边栏。 */
  standalone?: boolean;
  /** 隐藏画布上的打印/导出/批量/签名动作条（独立页里没 SDK，点了必然失败）。 */
  hideActions?: boolean;
  /** 表名，生成示例内容时当抬头用。 */
  tableName?: string;
}

/**
 * 插件内全屏三栏编辑器（覆盖当前面板；配合 setHostContainerSize(Large) 放大
 * 飞书宿主容器）。用实时 SDK 数据，不做快照/云同步——就是把侧边栏编辑器
 * 摊成组件栏 / 画布 / 属性面板三栏。对标竞品在飞书窗口内的编辑器。
 */
export function FullscreenOverlay({
  context,
  recordId,
  template,
  fieldMetas,
  onUpdate,
  onClose,
  staticData,
  standalone,
  hideActions,
  tableName,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(template.sections[0]?.id ?? null);
  const guide = useEditorTour();
  const history = useTemplateHistory(template, onUpdate);
  const [canvasPreview, setCanvasPreview] = useState(false);
  /** 画布上选中的格子区域（在某张表格里）。 */
  const [cellSel, setCellSel] = useState<({ id: string } & Rect) | null>(null);

  const updateSection = (sec: ContractSection) => {
    onUpdate({ sections: updateSectionDeep(template.sections, sec.id, sec) });
  };
  /** 画布上点了区块之间 / 空栏里的「+」：选好类型后插在那个位置。 */
  const [insertAt, setInsertAt] = useState<{ index: number; parent: { id: string; col: number } | null } | null>(null);
  const insertSectionAt = (type: ContractSectionType) => {
    if (insertAt === null) return;
    const block = createBlock(type);
    if (!block) return;
    onUpdate({ sections: insertAtDeep(template.sections, block, insertAt.index, insertAt.parent?.id, insertAt.parent?.col) });
    setInsertAt(null);
    setSelectedId(block.id);
  };
  /**
   * 点字段插入。选中的是标题/文本段落就直接续写进去（用户多半正在那儿排版），
   * 否则在选中块后面补一个文本段落装它 —— 总之不能让用户点了没反应。
   */
  const insertFieldRef = (fieldName: string) => {
    const token = `{{${fieldName}}}`;
    // 画布上正选着某个格子：字段落进那一格（表头下面的样板行就是这么绑的）
    if (cellSel && cellSel.id === selectedId) {
      onUpdate({ sections: appendCellText(template.sections, cellSel.id, cellSel.r0, cellSel.c0, token) });
      return;
    }
    const cur = selectedId ? findSectionDeep(template.sections, selectedId) : null;
    if (cur && (cur.type === 'header' || cur.type === 'text_block')) {
      const joiner = cur.content && !cur.content.endsWith(' ') ? ' ' : '';
      updateSection({ ...cur, content: `${cur.content}${joiner}${token}` });
      return;
    }
    const sec = createDefaultSection('text_block');
    const filled = { ...sec, content: token } as ContractSection;
    const next = cur ? insertAfter(template.sections, cur.id, filled).sections : [...template.sections, filled];
    onUpdate({ sections: next });
    setSelectedId(filled.id);
  };

  /**
   * 一张绑好关联字段的明细表：表头一行写子表字段名，样板行每列绑对应字段，
   * 插进去就能印。以前只给一张空表，用户还得去右侧一列一列翻下拉。
   */
  const buildLinkTable = (field: FieldMeta, subFields: FieldMeta[]): ContractSection => {
    const cols = subFields.slice(0, 6);
    const n = Math.max(2, cols.length);
    const base = createFreeTable(2, n);
    const header = base.rows[0];
    header.cells = header.cells.map((c, i) => ({ ...c, content: cols[i]?.name ?? '', bold: true, shaded: true }));
    const columnFields: Record<number, string> = {};
    cols.forEach((f, i) => {
      columnFields[i] = f.id;
    });
    return {
      type: 'free_table',
      id: genId(),
      colWidths: base.colWidths,
      rows: base.rows,
      borderPx: 1,
      fontSizePx: null,
      repeat: { source: 'link', linkFieldId: field.id, rowIndex: 1, columnFields },
    } as ContractSection;
  };
  const insertLinkTable = (field: FieldMeta, subFields: FieldMeta[]) => {
    const table = buildLinkTable(field, subFields);
    const next = selectedId ? insertAfter(template.sections, selectedId, table).sections : [...template.sections, table];
    onUpdate({ sections: next });
    setSelectedId(table.id);
  };
  /**
   * 点了左栏的某个子表字段：
   *   画布上选着格子 → 那一格所在的列绑上它（表还没认这个关联字段就顺手认上，
   *                     样板行 = 选中的那一行）
   *   没选格子       → 插一张只有这一列的明细表
   */
  const insertLinkField = (link: FieldMeta, sub: FieldMeta) => {
    if (cellSel) {
      const sec = findSectionDeep(template.sections, cellSel.id);
      if (sec && sec.type === 'free_table') {
        const t = sec as FreeTableSection;
        const rep = t.repeat && (t.repeat.source ?? 'link') === 'link' && t.repeat.linkFieldId === link.id
          ? t.repeat
          : { source: 'link' as const, linkFieldId: link.id, rowIndex: cellSel.r0, columnFields: {} };
        const columnFields = { ...(rep.columnFields || {}), [cellSel.c0]: sub.id };
        updateSection({ ...t, repeat: { ...rep, columnFields } } as ContractSection);
        return;
      }
    }
    insertLinkTable(link, [sub]);
  };

  // ── 画布动作 → 模板改动（语义统一在 contract/canvasOps） ──
  const onTableOp = (msg: TableOpMessage) => {
    
    onUpdate({ sections: applyTableOp(template.sections, msg) });
  };
  const onCellText = (id: string, r: number, c: number, text: string) => {
    
    onUpdate({ sections: applyCellText(template.sections, id, r, c, text) });
  };
  const onInsertAfter = (afterId: string, blockType: string, text?: string) => {
    const block = createBlock(blockType);
    if (!block) return;
    if (text && block.type === 'text_block') (block as { content: string }).content = text;
    const { sections, id } = insertAfter(template.sections, afterId, block);
    onUpdate({ sections });
    setSelectedId(id);
  };

  /** 画布上双击改完标题/文本段落的文字。以前全屏编辑器漏接了这条消息，改了不存。 */
  const onSectionText = (id: string, text: string) => {
    onUpdate({ sections: applySectionText(template.sections, id, text) });
  };
  const onCellPatch = (patch: Partial<CellProps>) => {
    if (!cellSel) return;
    onUpdate({ sections: applyCellPatch(template.sections, cellSel.id, cellSel, patch) });
  };
  const onSectionDuplicate = (id: string) => {
    const { sections, id: newId } = duplicateSection(template.sections, id);
    onUpdate({ sections });
    if (newId) setSelectedId(newId);
  };
  const onRebind = (id: string, from: string, to: string) => {
    onUpdate({ sections: rebindField(template.sections, id, from, to, fieldMetas) });
  };
  const onSectionConvert = (id: string, blockType: string) => {
    onUpdate({ sections: convertSection(template.sections, id, blockType) });
  };

  /** 一键把空模板填成一份可直接打印的示例。 */
  const insertDemo = () => {
    const secs = buildDemoSections(fieldMetas, tableName ?? '');
    onUpdate({ sections: secs });
    setSelectedId(secs[0]?.id ?? null);
  };

  const addSection = (type: ContractSectionType | 'columns') => {
    const sec = createBlock(type);
    if (!sec) return;
    onUpdate({ sections: [...template.sections, sec] });
    setSelectedId(sec.id);
  };
  const moveSection = (id: string, dir: -1 | 1) => {
    onUpdate({ sections: moveSectionDeep(template.sections, id, dir) });
  };
  const deleteSection = (id: string) => {
    onUpdate({ sections: removeSectionDeep(template.sections, id) });
    setSelectedId((cur) => (cur === id ? null : cur));
  };
  const reorderSection = (id: string, targetId: string, before: boolean) => {
    onUpdate({ sections: reorderDeep(template.sections, id, targetId, before) });
    setSelectedId(id);
  };

  const selected = selectedId ? findSectionDeep(template.sections, selectedId) : null;

  function renderBlockEditor(sec: ContractSection) {
    const common = { onChange: updateSection as never, fieldMetas };
    if (sec.type === 'columns') return <ColumnsEditor section={sec} onChange={updateSection} />;
    switch (sec.type) {
      case 'header': return <HeaderEditor section={sec} {...common} />;
      case 'info_grid': return <InfoGridEditor section={sec} {...common} />;
      case 'detail_table': return <DetailTableEditor section={sec} {...common} />;
      case 'free_table': return <FreeTableEditor section={sec} {...common} />;
      case 'text_block': return <TextBlockEditor section={sec} {...common} />;
      case 'signature': return <SignatureEditor section={sec} {...common} />;
      case 'attachment': return <AttachmentEditor section={sec} {...common} />;
      case 'code': return <CodeEditor section={sec} {...common} />;
      case 'rich_text': return <RichTextEditor section={sec} {...common} />;
      case 'divider': return <DividerEditor section={sec} onChange={updateSection as never} />;
    }
  }

  const setRule = (secId: string, rule: VisibilityRule | undefined) => {
    const next = { ...(template.visibility || {}) };
    if (rule) next[secId] = rule;
    else delete next[secId];
    onUpdate({ visibility: next });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'var(--surface-1)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 顶栏 */}
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <Button size="small" type="primary" icon={<CompressOutlined />} onClick={onClose} data-tour="done">
          {standalone ? '完成并关闭' : '完成编辑'}
        </Button>
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
        <Input
          size="small"
          variant="borderless"
          style={{ width: 240, fontWeight: 700, fontSize: 14, padding: '0 4px' }}
          value={template.name}
          placeholder="模板名称"
          onChange={(e) => onUpdate({ name: e.target.value })}
        />
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
        {/* 编辑看结构（字段显示成 {{胶囊}}），预览看数据。
            两者混在一起的话，用户分不清哪段是绑定的字段、哪段是写死的文字，
            「为什么换条记录这里不变」就是这么来的。 */}
        <Tooltip title="撤销 ⌘Z">
          <Button size="small" icon={<UndoOutlined />} disabled={!history.canUndo} onClick={history.undo} />
        </Tooltip>
        <Tooltip title="重做 ⇧⌘Z">
          <Button size="small" icon={<RedoOutlined />} disabled={!history.canRedo} onClick={history.redo} />
        </Tooltip>
        <Segmented
          size="small"
          data-tour="mode"
          value={canvasPreview ? 'preview' : 'edit'}
          onChange={(v) => setCanvasPreview(v === 'preview')}
          options={[
            { label: '编辑', value: 'edit' },
            { label: '预览', value: 'preview' },
          ]}
        />
        <span style={{ flex: 1 }} />
        <ShareToggle template={template} onUpdate={onUpdate} />
        <Tooltip
          title={
            standalone
              ? '改动自动保存，回到多维表格时侧边栏会自动拿到最新版。打印 / 导出 / 发起签名在侧边栏完成。'
              : '改动自动保存。完成后回面板打印 / 导出 / 发起签名。'
          }
        >
          <Typography.Text type="secondary" style={{ fontSize: 11.5, cursor: 'default' }}>
            ● 自动保存
          </Typography.Text>
        </Tooltip>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* 左：组件栏 */}
        <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border)', padding: 12, overflowY: 'auto' }}>
          {/* 字段在最上面：用户想的是「把客户名称印上去」，不是「放一个信息网格」 */}
          <div data-tour="fields">
          <FieldPalette
            fieldMetas={fieldMetas}
            onInsertField={insertFieldRef}
            onInsertLinkTable={insertLinkTable}
            onInsertLinkField={insertLinkField}
            hasCellSelection={!!cellSel}
          />
          </div>
          <div className="pp-panel-title">组件</div>
          <div className="pp-chip-grid" data-tour="blocks">
            {INSERTABLE_SECTION_TYPES.map((type) => (
              <button key={type} className="pp-chip" onClick={() => addSection(type)} title={`添加${SECTION_TYPE_LABELS[type]}`}>
                {SECTION_ICONS[type]}
                <span>{SECTION_TYPE_LABELS[type]}</span>
              </button>
            ))}
          </div>
          <div className="pp-panel-title" style={{ margin: '16px 0 8px' }}>印章 · 签名区域</div>
          <OverlaysEditor
            overlays={template.overlays || []}
            sections={template.sections}
            onSectionsChange={(sections) => onUpdate({ sections })}
            onChange={(overlays: OverlayItem[]) => onUpdate({ overlays })}
          />
        </div>

        {/* 中：画布 */}
        <div className="pp-desk pp-scroll" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '16px 28px 40px' }}>
          {/* 空模板对新手是最难的一步：一张白纸加一列按钮，不知道先放什么。
              给一份用真实字段拼好的示例，边看边改。 */}
          {template.sections.length === 0 && (
            <div
              style={{
                marginBottom: 12,
                padding: '12px 14px',
                border: '1px dashed var(--border)',
                borderRadius: 10,
                background: 'var(--surface-1)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>还是一张白纸？</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                  用这张表的字段生成一份示例（抬头 + 信息网格 + 明细表 + 签章区），改比从头搭快得多。
                </div>
              </div>
              <Button type="primary" size="small" onClick={insertDemo}>
                生成示例内容
              </Button>
            </div>
          )}
          <div style={{ maxWidth: 'calc(210mm + 2px)', margin: '0 auto' }} data-tour="canvas">
            <CanvasHints onOpenGuide={guide.show} />
          </div>
          <ContractPreview
            onUndo={history.undo}
            onRedo={history.redo}
            context={context}
            recordId={recordId}
            template={template}
            fieldMetas={fieldMetas}
            staticData={staticData}
            hideActions={hideActions}
            canvasPreview={canvasPreview}
            overlayEdit
            selectedSectionId={selectedId}
            onSectionSelect={(id) => {
              setSelectedId(id);
              if (cellSel && cellSel.id !== id) setCellSel(null);
            }}
            onSectionTextChange={onSectionText}
            onRebind={onRebind}
            onSectionDuplicate={onSectionDuplicate}
            onSectionConvert={onSectionConvert}
            onCellSelect={setCellSel}
            onSectionMove={moveSection}
            onSectionDelete={deleteSection}
            onSectionReorder={reorderSection}
            onSectionInsert={(i, parent) => setInsertAt({ index: i, parent: parent ?? null })}
            onTableOp={onTableOp}
            onCellText={onCellText}
            onInsertAfter={onInsertAfter}
            onOverlayPatch={(id, patch) => {
              const overlays = (template.overlays || []).map((o) => (o.id === id ? { ...o, ...patch } : o));
              onUpdate({ overlays });
            }}
          />
        </div>

        {/* 右：属性面板 */}
        <div data-tour="props" style={{ width: 320, flexShrink: 0, borderLeft: '1px solid var(--border)', padding: 14, overflowY: 'auto' }}>
          <div className="pp-panel-title">
            {selected ? `属性 · ${SECTION_TYPE_LABELS[selected.type]}` : '属性'}
          </div>
          {selected ? (
            <>
              {cellSel && cellSel.id === selected.id && selected.type === 'free_table' && (
                <CellStylePanel section={selected as FreeTableSection} rect={cellSel} onPatch={onCellPatch} />
              )}
              {renderBlockEditor(selected)}
              <VisibilityRuleEditor
                rule={template.visibility?.[selected.id]}
                fieldMetas={fieldMetas}
                onChange={(rule) => setRule(selected.id, rule)}
              />
            </>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              在画布中点击一个区块进行编辑
            </Typography.Text>
          )}
          <div className="pp-panel-title" style={{ margin: '18px 0 8px' }}>排版设置</div>
          <ContractSettings settings={template.settings} onChange={(settings) => onUpdate({ settings })} />
        </div>
      </div>
      <EditorTour open={guide.open} onClose={guide.close} />
      <Modal
        open={insertAt !== null}
        onCancel={() => setInsertAt(null)}
        title={insertAt?.parent ? `在第 ${insertAt.parent.col + 1} 栏里插入区块` : `在第 ${(insertAt?.index ?? 0) + 1} 个位置插入区块`}
        footer={null}
        width={360}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {INSERTABLE_SECTION_TYPES.map((type) => (
            <Button key={type} size="small" onClick={() => insertSectionAt(type)}>
              {SECTION_TYPE_LABELS[type]}
            </Button>
          ))}
        </div>
      </Modal>
    </div>
  );
}
